import { useState, useEffect, useCallback } from 'react';
import {
  getTintedDefaultLogo,
  isDefaultAccent,
  DEFAULT_LOGO_PATH,
  DEFAULT_ACCENT_COLOR,
} from '../utils/tintLogo';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || '';
const DEFAULT_LOGO = DEFAULT_LOGO_PATH;

// Custom-Logo-URL aus localStorage. Liefert null wenn der User kein
// eigenes Logo hochgeladen hat – dann muss das (ggf. gefärbte)
// Standard-Logo verwendet werden.
const getCustomLogoUrlFromStorage = () => {
  const brandingLogo = localStorage.getItem('branding-logo');
  if (brandingLogo) {
    return `${API_BASE_URL}${brandingLogo}`;
  }
  return null;
};

const getAccentFromStorage = () => {
  return localStorage.getItem('branding-accent') || DEFAULT_ACCENT_COLOR;
};

// Initialer Logo-URL für useState. Wir starten mit dem Standard-PNG bzw.
// dem Custom-Logo. Das eingefärbte Logo (Data-URL) wird asynchron im
// useEffect berechnet und ersetzt den Standard, sobald es bereit ist.
const getInitialLogoUrl = () => {
  const custom = getCustomLogoUrlFromStorage();
  if (custom) return custom;
  return DEFAULT_LOGO;
};

// Chat-Icon-URL aus localStorage generieren
const getChatIconUrlFromStorage = () => {
  const brandingChatIcon = localStorage.getItem('branding-chaticon');
  if (brandingChatIcon) {
    return `${API_BASE_URL}${brandingChatIcon}`;
  }
  return null;
};

export function useBranding() {
  const [logoUrl, setLogoUrl] = useState(getInitialLogoUrl);
  const [chatIconUrl, setChatIconUrl] = useState(getChatIconUrlFromStorage);
  const [loaded, setLoaded] = useState(!!localStorage.getItem('branding-logo'));

  // Funktion zum Aktualisieren des Logos und Chat-Icons
  const updateLogo = useCallback(() => {
    const customLogoUrl = getCustomLogoUrlFromStorage();
    const newChatIconUrl = getChatIconUrlFromStorage();
    setChatIconUrl(newChatIconUrl);
    setLoaded(true);

    if (customLogoUrl) {
      // Custom Logo: direkt verwenden, keine Färbung
      setLogoUrl(customLogoUrl);
      return;
    }

    // Kein Custom Logo: Standard-Logo, ggf. eingefärbt
    const accent = getAccentFromStorage();
    if (isDefaultAccent(accent)) {
      setLogoUrl(DEFAULT_LOGO);
      return;
    }

    // Sofort den Standard zeigen, asynchron eingefärbte Version laden
    setLogoUrl((prev) => prev || DEFAULT_LOGO);
    getTintedDefaultLogo(accent)
      .then((url) => {
        // Nur setzen, wenn die Akzentfarbe in der Zwischenzeit nicht
        // bereits wieder geändert wurde.
        if (getAccentFromStorage() === accent && !getCustomLogoUrlFromStorage()) {
          setLogoUrl(url);
        }
      })
      .catch(() => setLogoUrl(DEFAULT_LOGO));
  }, []);

  useEffect(() => {
    // Initial: Logo aus localStorage lesen
    updateLogo();

    // Listener für Änderungen am LocalStorage (von anderen Tabs)
    const handleStorageChange = (e) => {
      if (
        e.key === 'branding-logo' ||
        e.key === 'branding-chaticon' ||
        e.key === 'branding-accent' ||
        e.key === null
      ) {
        updateLogo();
      }
    };

    // Custom Event für Logo-Änderungen innerhalb der gleichen Tab
    const handleBrandingUpdate = () => {
      updateLogo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('branding-updated', handleBrandingUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('branding-updated', handleBrandingUpdate);
    };
  }, [updateLogo]);

  return { logoUrl, chatIconUrl, loaded };
}

// Hilfsfunktion zum Auslösen eines Branding-Updates
export function triggerBrandingUpdate() {
  window.dispatchEvent(new Event('branding-updated'));
}
